import React from 'react';
import StatCard from '../StatCard';

function TestOverview({ st, getPF, getStats, overviewLocName, setOverviewLocName, onStartTest, onViewDevice, userKey, SYS_LOCS, IC, DM, OW, darkMode, bDevs, testView, resumeDraft, dismissDraft, isReadOnly, FilterSelect }) {
  const availLocs = (st.locations || []).filter(l => !l.archived && !SYS_LOCS.includes(l.name));

  const locInfo = React.useMemo(() => {
    if (!overviewLocName) return null;
    if (overviewLocName === 'In Stock') return { isActive: true, batchNum: null, dispatchedAt: null, forecastStart: null, forecastEnd: null };
    const loc = (st.locations || []).find(l => (l.name || l.customer) === overviewLocName);
    if (!loc) return null;
    const activeBatch = (loc.batches || []).find(b => b.status === 'active');
    const fmt = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;
    const fmtShort = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : null;
    return {
      isActive: loc.status === 'Active',
      batchNum: activeBatch?.number ?? null,
      dispatchedAt: activeBatch?.dispatchedAt ? fmt(activeBatch.dispatchedAt) : null,
      forecastStart: loc.forecastStart ? fmtShort(loc.forecastStart) : null,
      forecastEnd: loc.forecastEnd ? fmtShort(loc.forecastEnd) : null,
    };
  }, [st.locations, overviewLocName]);

  const locDevices = React.useMemo(() => {
    const devs = (st.devices || []).filter(d => d.status !== 'Retired' && d.status !== 'Pending Retirement');
    if (!overviewLocName) return devs;
    return devs.filter(d => d.location === overviewLocName || (d.status === 'In Testing' && bDevs.find(b => b.id === d.id)));
  }, [st.devices, overviewLocName, bDevs]);

  const stats = React.useMemo(() => {
    const total = locDevices.length;
    const passed = locDevices.filter(d => getStats(d).overall === 'pass').length;
    const failed = locDevices.filter(d => getStats(d).overall === 'fail').length;
    const pending = locDevices.filter(d => d.status === 'In Testing').length;
    const notStarted = locDevices.filter(d => getStats(d).overall === null && d.status !== 'In Testing').length;
    return { total, passed, failed, pending, notStarted };
  }, [locDevices, getStats]);

  const [devSearch, setDevSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('All');

  const filteredDevices = React.useMemo(() => {
    return locDevices.filter(d => {
      if (devSearch && !d.serial?.toLowerCase().includes(devSearch.toLowerCase()) && !d.type?.toLowerCase().includes(devSearch.toLowerCase())) return false;
      if (statusFilter !== 'All') {
        const s = getStats(d);
        if (statusFilter === 'Passed' && s.overall !== 'pass') return false;
        if (statusFilter === 'Failed' && s.overall !== 'fail') return false;
        if (statusFilter === 'Pending' && d.status !== 'In Testing') return false;
        if (statusFilter === 'Not started' && !(s.overall === null && d.status !== 'In Testing')) return false;
      }
      return true;
    });
  }, [locDevices, devSearch, statusFilter, getStats]);

  const draft = st.testDrafts?.[userKey];

  return (
    <div>
      {/* Top action row: Test devices button */}
      {!isReadOnly && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <button
            onClick={onStartTest}
            disabled={!overviewLocName}
            title={!overviewLocName ? 'Select a location first' : undefined}
            className="btn btn-primary"
            style={{ fontSize: 13, padding: '8px 18px', fontWeight: 600, opacity: !overviewLocName ? 0.5 : 1, cursor: !overviewLocName ? 'not-allowed' : 'pointer' }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Assign devices
          </button>
        </div>
      )}

      {/* Location bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, padding: '8px 14px', borderRadius: 8, background: 'var(--surface-default)', border: '1px solid var(--border-default)' }}>
        {/* Left: label + dropdown */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '1px', textTransform: 'uppercase', flexShrink: 0 }}>Location</span>
          <select
            className="input"
            value={overviewLocName}
            onChange={e => setOverviewLocName(e.target.value)}
            style={{ minWidth: 200, fontSize: 13, fontWeight: 500, color: 'var(--text-default)' }}
          >
            <option value="">— Select a location —</option>
            <option value="In Stock">In Stock</option>
            {availLocs.map(l => (
              <option key={l.name || l.customer} value={l.name || l.customer}>
                {l.name || l.customer}
              </option>
            ))}
          </select>
        </div>

        {/* Right: location status info */}
        {locInfo && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 20, fontSize: 12, color: 'var(--text-muted)' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: locInfo.isActive ? 'var(--success-icon)' : 'var(--text-ghost)', display: 'inline-block', flexShrink: 0 }} />
              <span style={{ color: 'var(--text-default)', fontWeight: 500 }}>
                {locInfo.isActive ? 'Active' : 'Inactive'}
                {locInfo.batchNum !== null && <> · Batch {locInfo.batchNum}</>}
                {locInfo.dispatchedAt && <> · {locInfo.dispatchedAt}</>}
              </span>
            </span>
            {(locInfo.forecastStart || locInfo.forecastEnd) && (
              <span>
                Forecast: {locInfo.forecastStart}{locInfo.forecastEnd ? ` – ${locInfo.forecastEnd}` : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Draft resume banner */}
      {draft && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: 'var(--warning-fill)', border: '1px solid var(--warning-border)' }}>
          <span className="pill pill-warning" style={{ fontSize: 11, fontWeight: 700, flexShrink: 0 }}>DRAFT</span>
          <span style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1 }}>
            {draft.bDevs.length} device{draft.bDevs.length !== 1 ? 's' : ''}
            {draft.bMode === 'firmware' ? ` · FW Test v${draft.bFwVersion || '?'}` : ''}
            {' · saved '}{new Date(draft.savedAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
          </span>
          <button onClick={resumeDraft} className="btn btn-primary" style={{ fontSize: 12, flexShrink: 0 }}>Resume</button>
          <button onClick={dismissDraft} style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>✕</button>
        </div>
      )}

      {!overviewLocName ? (
        <div style={{ padding: '60px 24px', textAlign: 'center', border: '1px dashed var(--border-default)', borderRadius: 'var(--radius-lg)', background: 'var(--surface-default)' }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', marginBottom: 12 }}>
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-default)', marginBottom: 6 }}>Select a location to continue</div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Choose a location from the dropdown above to view its devices and start a test session.</div>
        </div>
      ) : (<>
      {/* Stats tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 20 }}>
        <StatCard label="Total Devices" value={stats.total} />
        <StatCard label="Passed" value={stats.passed} valueColor="var(--success-text)" />
        <StatCard label="Failed" value={stats.failed} valueColor="var(--danger-text)" />
        <StatCard label="Pending" value={stats.pending} valueColor="var(--warning-text)" />
        <StatCard label="Not Started" value={stats.notStarted} valueColor="var(--text-muted)" />
      </div>

      {/* Search + filter toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <div className="input-with-icon" style={{ flex: '1 1 180px', maxWidth: 300 }}>
          <svg className="input-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            className="input"
            placeholder="Search location..."
            value={devSearch}
            onChange={e => setDevSearch(e.target.value)}
          />
          {devSearch && (
            <button onClick={() => setDevSearch('')} aria-label="Clear search" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 0, display: 'flex', alignItems: 'center' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          )}
        </div>
        <FilterSelect
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'All', label: 'All statuses' },
            { value: 'Passed', label: 'Passed' },
            { value: 'Failed', label: 'Failed' },
            { value: 'Pending', label: 'Pending' },
            { value: 'Not started', label: 'Not started' },
          ]}
          darkMode={darkMode}
          OW={OW}
          DM={DM}
        />
      </div>

      {/* Device list */}
      <div style={{ border: '1px solid var(--border-default)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
        {/* Column header */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: 12, padding: '10px 16px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-default)' }}>
          {[['DEVICE / TYPE', 'left'], ['CHECKS', 'right'], ['STATUS', 'right'], ['', 'right']].map(([label, align]) => (
            <div key={label} style={{ fontSize: 'var(--font-size-overline)', fontWeight: 'var(--font-weight-bold)', color: 'var(--text-subtle)', letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', textAlign: align }}>
              {label}
            </div>
          ))}
        </div>

        {filteredDevices.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
            No devices{overviewLocName ? ` at ${overviewLocName}` : ''}
          </div>
        ) : (
          filteredDevices.map((d, i) => {
            const s = getStats(d);
            const defs = getPF(d.type);
            const latestResult = (st.testResults || [])
              .filter(r => r.deviceId === d.id)
              .sort((a, b) => new Date(b.date) - new Date(a.date))[0];
            const checks = latestResult?.checks || {};
            const isInTestingSession = d.status === 'In Testing';

            const dotColor = isInTestingSession
              ? 'var(--warning-text)'
              : s.overall === 'pass' ? 'var(--success-text)'
              : s.overall === 'fail' ? 'var(--danger-text)'
              : 'var(--text-ghost)';

            let statusEl;
            if (isInTestingSession) {
              statusEl = <span className="pill pill-warning" style={{ fontSize: 11 }}>Pending</span>;
            } else if (s.overall === 'pass') {
              statusEl = <span className="pill pill-success" style={{ fontSize: 11 }}>Passed</span>;
            } else if (s.overall === 'fail') {
              statusEl = <span className="pill pill-danger" style={{ fontSize: 11 }}>Failed</span>;
            } else {
              statusEl = <span className="pill pill-neutral" style={{ fontSize: 11 }}>Not started</span>;
            }

            const checkFraction = defs.length > 0
              ? `${(s.passCount || 0) + (s.failCount || 0)}/${defs.length}`
              : '0/0';

            return (
              <div
                key={d.id}
                style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--surface-default)', borderBottom: i < filteredDevices.length - 1 ? '1px solid var(--border-default)' : 'none', transition: 'background 0.12s' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
                onMouseLeave={e => e.currentTarget.style.background = 'var(--surface-default)'}
              >
                {/* Col 1: device identity */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <span style={{ width: 7, height: 7, borderRadius: '50%', background: dotColor, display: 'inline-block', flexShrink: 0 }} />
                  <span style={{ fontWeight: 'var(--font-weight-bold)', fontSize: 'var(--font-size-body)', color: 'var(--text-primary)', fontFamily: 'monospace' }}>{d.serial}</span>
                  <span style={{ fontWeight: 400, fontSize: 'var(--font-size-label)', color: 'var(--text-subtle)' }}>· {d.type}</span>
                </div>

                {/* Col 2: check dots + fraction */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {defs.map(def => {
                    const v = checks[def.id];
                    const color = v === 'pass' ? 'var(--success-icon)' : v === 'fail' ? 'var(--danger-text)' : v === 'na' ? 'var(--text-ghost)' : 'var(--border-hover)';
                    return <span key={def.id} className="check-dot" title={def.label + ': ' + (v || 'not tested')} style={{ background: color }} />;
                  })}
                  <span style={{ fontSize: 11, color: 'var(--text-subtle)', marginLeft: 4, fontFamily: 'monospace' }}>{checkFraction}</span>
                </div>

                {/* Col 3: status pill */}
                {statusEl}

                {/* Col 4: action */}
                <button onClick={() => onViewDevice(d)} className="btn btn-ghost" style={{ fontSize: 12, padding: '5px 12px' }}>
                  View checks
                </button>
              </div>
            );
          })
        )}
      </div>
      </>)}
    </div>
  );
}

export default TestOverview;
